/* eslint no-useless-escape:0 */
import { makeUniqueArray, valueExists } from '@mw/helpers';

import {
  Country,
  CustomerChatbotEnabledStatus,
  MediaTags,
  MediaTypes,
} from '~/constants';

import { LocalStorage } from '~/helpers/LocalStorage';

const { REACT_APP_DOMAIN } = process.env;

/**
 * Helper function that returns a true or false value depending on whether or not the user is browsing with a mobile.
 * (true is mobile or tablet)
 * @returns {boolean}
 * taken from: https://stackoverflow.com/a/11381730/3880019
 */
export const isMobileOrTablet = () => {
  let check = false;

  ((agent) => {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        agent
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        agent.substr(0, 4)
      )
    ) {
      check = true;
    }
  })(navigator.userAgent || navigator.vendor || window.opera);

  return check;
};

export const escapeSpecialCharactersRegExp = (string) =>
  new RegExp(string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

export const getCountryNameFromCode = (code) => {
  const country = Country.find((c) => c.code === code);

  if (country) {
    return country.country;
  }

  return 'Undefined';
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  if (Number.isNaN(bytes) || !bytes) {
    return '';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Checks origin of PostMessage
 * @param {String} origin
 * @returns {Boolean} - given origin is allowed or not
 */
export const isPostMessageOriginAllowed = (origin) => {
  // MW origins (https)
  const mwOrigins = (process.env.REACT_APP_MW_FRONT_ORIGINS || '').split(',');

  // add both http and https to allowed:
  const allowedOrigins = mwOrigins.reduce((acc, mwOrigin) => {
    acc.push(mwOrigin);
    acc.push(mwOrigin.replace(/^https:/, 'http:'));

    return acc;
  }, []);

  return allowedOrigins.includes(origin);
};

export const sortCustomersByLastMessageDesc = (a, b) => {
  const aLastMessageTimeEmpty = a.last_message_time === undefined;

  const bLastMessageTimeEmpty = b.last_message_time === undefined;

  // Different
  if (aLastMessageTimeEmpty !== bLastMessageTimeEmpty) {
    return aLastMessageTimeEmpty ? 1 : -1;
  }

  // Both empty
  if (aLastMessageTimeEmpty || bLastMessageTimeEmpty) {
    return 1;
  }

  // Both not empty
  const dateA = new Date(a.last_message_time).getTime();
  const dateB = new Date(b.last_message_time).getTime();

  return dateB > dateA ? 1 : -1;
};

export const sortCustomersByLastMessageAsc = (a, b) =>
  sortCustomersByLastMessageDesc(a, b) === 1 ? -1 : 1;

/**
 * Checks if arrays are equal by length and items without duplicates
 * @param arrA {T[]}
 * @param arrB {T[]}
 * @returns {boolean}
 */
export const areArraysEqual = (arrA, arrB) => {
  if (arrA.length !== arrB.length) return false;

  const uniqueArrA = makeUniqueArray(arrA);
  const uniqueArrB = makeUniqueArray(arrB);

  if (uniqueArrA.length !== uniqueArrB.length) return false;

  return (
    uniqueArrA.every((item) => uniqueArrB.includes(item)) &&
    uniqueArrB.every((item) => uniqueArrA.includes(item))
  );
};

const findLastArrayPrototypeAvailable = valueExists(Array.prototype.findLast);

/**
 * This method is like _.find except that it iterates over elements of a collection from right to left
 * @param arr {T[]}
 * @param predicate
 * @returns {T}
 */
export const findLast = (arr, predicate) => {
  if (findLastArrayPrototypeAvailable) return arr.findLast(predicate);

  const arrLength = arr.length;

  for (let i = arrLength - 1; i >= 0; i -= 1) {
    const currentValue = arr[i];
    const predicateResult = predicate(currentValue, i, arr);

    if (predicateResult) {
      return currentValue;
    }
  }

  return undefined;
};

export const checkIsIframe = () => window.self !== window.top;

export const checkIsSafari = () =>
  navigator.userAgent.includes('Safari') &&
  navigator.vendor.includes('Apple Computer');

export const getMWDomain = () => LocalStorage.getOrigin();

export const generateBaseURL = () => {
  const origin = getMWDomain();

  return origin ? `${origin}/app/chats` : `${REACT_APP_DOMAIN}/app/chats`;
};

export const makePath = (initialPath) => {
  const isIframe = checkIsIframe();

  return isIframe ? generateBaseURL() + initialPath : initialPath;
};

export const isChatbotEnabled = (customer, channel_id) => {
  const chatbotStatusForChannel = customer?.chatbot_enabled?.[channel_id];

  return (
    chatbotStatusForChannel === CustomerChatbotEnabledStatus.ENABLED_AUTO ||
    chatbotStatusForChannel === CustomerChatbotEnabledStatus.ENABLED_MANUALLY
  );
};

export const mapTagToMediaType = {
  [MediaTags.IMAGE]: MediaTypes.IMAGE,
  [MediaTags.VIDEO]: MediaTypes.VIDEO,
};

export const eventDebounceWrapper = (func, timeout, selector) => {
  let timer;

  return (e) => {
    const value = selector(e);

    clearTimeout(timer);
    timer = setTimeout(() => {
      func(value);
    }, timeout);
  };
};

export const getIsMac = () => {
  const userAgentData = navigator.userAgent;

  return userAgentData.toLowerCase().includes('macintosh');
};

const arabicOrHebrew = /[\u0600-\u06FF\u0590-\u05FF]/;
export const getTextDirection = ([firstLetter]) =>
  arabicOrHebrew.test(firstLetter) ? 'rtl' : 'ltr';

export const handleCopyClick = async (
  e,
  enqueueSnackbar,
  data,
  successMessage,
  failureMessage
) => {
  e.stopPropagation();

  try {
    await navigator.clipboard.writeText(data);
    enqueueSnackbar(successMessage || 'Data was successfully copied', {
      variant: 'success',
    });
  } catch (error) {
    enqueueSnackbar(failureMessage || 'Failed to copy data to clipboard', {
      variant: 'error',
    });
  }
};
